import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { SessionContextService } from "projects/packcert-web/src/app/context/session-context.service";
import { SpinnerService, Message, UtilsService } from "shared-components";
import { ExpedicionesService } from "../../../services/expediciones.service";

@Component({
    selector: 'app-new-edit-expedicion',
    templateUrl: './new-edit-expedicion.html'
})

export class NewEditExpedicion implements OnInit {
    @Input() expedicionId: number | null = null;
    @Input() denominacionId: number | null = null;
    @Input() generarExpedicionItems;

    @Output() refresh = new EventEmitter<boolean>();
    @Output() mensaje = new EventEmitter<string>();

    public newEditForm: UntypedFormGroup;
    public expediciones;
    public allUnits; // para ir guardando las unidades
    private savedToastTimeout;

    public deleteModal = false;
    public indexToDelete: number;

    public showError: boolean;
    public mensajes = new Array<Message>();

    constructor(
        private _fb: UntypedFormBuilder,
        private readonly _translate: TranslateService,
        private readonly _sessionContext: SessionContextService,
        private readonly _spinnerService: SpinnerService,
        private readonly utilsService: UtilsService,
        private readonly _expedicionesService: ExpedicionesService
    ) {}

    ngOnInit(): void {
        this.newEditForm = this._fb.group({
            fecha: ['', Validators.required],
            matricula: ['', Validators.required],
            albaran: ['', Validators.required],
        });

        if (!this.expedicionId) {
            this.newEditForm.get('fecha').patchValue(new Date());
            this.expediciones = this.generarExpedicionItems;
            /* Al abrir el modal de generar, se actualizan las unidades con el mismo valor de las disponibles.
            Guardamos también si el formato es correcto */
            this.allUnits = this.expediciones.map((el, index) => {
                this.expediciones[index].rightFormat = /^\d*$/.test((el.unidadesDisponibles || '').toString());
                return el.unidadesDisponibles || 0;
            });
        } else {
            this.getExpediciones();
        }
    }

    public get defaultLanguage() {
      return this._translate.getDefaultLang();
    }

    public get isSmallScreen(){
      return this._sessionContext.isSmallScreen();
    }

    private patchEditForm(resp) {
        this.newEditForm.patchValue({
            fecha: resp.fechaExpedicion,
            matricula: resp.matricula,
            albaran: resp.albaran
        });
    }

    /**
     * Al editar la expedición, mostrar el desglose de las confecciones
     */
    private getExpediciones() {
        this._spinnerService.showSpinner();
        this._expedicionesService.getExpedicionById(this.expedicionId).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this.expediciones = resp.lsExpediciones;

            if (this.expediciones?.length > 0) {
                this.allUnits = this.expediciones.map((el, index) => {
                    this.expediciones[index].rightFormat = /^\d*$/.test((el.unidades || '').toString());
                    return el.unidades || 0;
                });
                this.patchEditForm(resp);
            }
        }, (error) => {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
        });
    }

    /**
     * Actualizamos las unidades, además de si el formato es correcto
     */
    public updateUnits(index: number, units: string) {
        this.expediciones[index].rightFormat = /^\d*$/.test(units);
        // Teniendo en cuenta que son enteros, en caso de que el usuario introduzca separadores de millares
        const transformedUnits = units?.replace(/[,.]/g, '') || '';
        this.allUnits[index] = transformedUnits ? parseInt(transformedUnits) : '';
    }

    public close() {
        this.refresh.emit(false);
    }

    private prepareCommonFilters() {
        const mappedExpediciones = this.expediciones.map((expedicion, index) => {
            return {
                consumoId: !this.expedicionId ? expedicion.id : expedicion.consumoId,
                unidades: this.allUnits[index]
            }
        });

        const filters: any = {
            fechaExpedicion: this.newEditForm.get('fecha').value && this.newEditForm.get('fecha').value instanceof Date ? this.utilsService.parseGMTtoYYYYMMDD(this.newEditForm.get('fecha').value) : (this.newEditForm.get('fecha')?.value || ''),
            matricula: this.newEditForm.get('matricula')?.value || '',
            albaran: this.newEditForm.get('albaran')?.value || '',
            denominacionId: this.denominacionId,
            lsExpediciones: mappedExpediciones,
        };

        return filters;
    }

    private generateExpedicion() {
        const filters = this.prepareCommonFilters();

        this._spinnerService.showSpinner();
        this._expedicionesService.createExpedicion(filters).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this.mensaje.emit('global.success.itemCreated');
            this.refresh.emit(true);
        }, (error) => {
            this.showError = true;
            if (error.status === 420 && error.error.ApiErrorCode === 6247) {
                this.mensajes.push({ class: 'error', message: 'mantenimientoExpediciones.error.licencia' });
            } else {
                this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
            }
        });
    }

    private updateExpedicion() {
        const filters = this.prepareCommonFilters();
        filters.id = this.expedicionId;

        this._spinnerService.showSpinner();
        this._expedicionesService.updateExpedicion(filters).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this.mensaje.emit('global.success.itemUpdated');
            this.refresh.emit(true);
        }, (error) => {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
        });

    }

    public save() {
        if (this.savedToastTimeout) {
            clearTimeout(this.savedToastTimeout);
        }

        const unitsFilled = this.allUnits.every(unit => unit || unit === 0);
        const rightFormat = this.expediciones.every(expedicion => expedicion.rightFormat);
        const zeroUnits = this.allUnits.some(unit => unit === 0);
        const unitsNotExceeded = this.allUnits.every((unit, index) => (unit || 0) <= (this.expediciones[index].unidadesDisponibles || 0));

        if (this.newEditForm.invalid || 
            !unitsFilled || 
            !rightFormat ||
            zeroUnits ||
            (unitsFilled && !unitsNotExceeded)) {
            this.showError = true;

            // Mostrar mensaje en caso de campos requeridos no rellenados (form superior o unidades)
            if (this.newEditForm.invalid || !unitsFilled) {
                this.mensajes.push({ class: 'error', message: 'global.error.required' });
            }

            // Mostrar mensaje en caso de formato incorrecto en unidades
            if (!rightFormat) {
                this.mensajes.push({ class: 'error', message: 'mantenimientoExpediciones.error.unitsFormat' });
            }

            // Mostrar mensaje de borrar confección si se introducen unidades a 0
            if (zeroUnits) {
                this.mensajes.push({ class: 'error', message: 'mantenimientoExpediciones.error.deleteConfeccion' });
            }

            // Mostrar mensaje en caso de superar las disponibles
            if (unitsFilled && !unitsNotExceeded) {
                this.mensajes.push({ class: 'error', message: 'mantenimientoExpediciones.error.unitsExceeded' });
            }        
            
            // Asegurar que el toast también se oculte tras validación de unidades
            this.savedToastTimeout = setTimeout(() => {
                this.showError = false;
                this.mensajes = [];
            }, 5000);
        } else {

            if (!this.expedicionId) {
                this.generateExpedicion();
            } else {
                this.updateExpedicion();
            }
        }
    }

    public showDeleteModal(index: number) {
        this.indexToDelete = index;
        this.deleteModal = true;
    }

    public delete() {
        this.allUnits.splice(this.indexToDelete, 1);
        this.expediciones.splice(this.indexToDelete, 1);
        this.deleteModal = false;
    }
}